import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import React, { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import {
	ROLES_AND_PERMISSION_ENABLED,
	TABLES_AND_STATIC_DOCS_ENABLED,
	TenantAuthorizationData,
	useAuthContext,
} from '../../context/auth';
import { useConfigContext } from '../../context/config';
import Logout from '../Logout';
import {
	AccountDetails,
	AddRole,
	EditRole,
	Integrations,
	ResourceGroups,
	Roles,
	ScriptLogs,
	StaticDocuments,
	Tables,
	Users,
} from './tenant';
import TenantsAPI, { TenantResponse } from './tenant/api/tenants';
import NonSsoLayout from './tenant/components/Layout';
import DataAutofillsPanel from './tenant/pages/Integrations/DataAutofillsPanel';
import ExternalRatersPanel from './tenant/pages/Integrations/ExternalRatersPanel';
import WebhooksPanel from './tenant/pages/Integrations/WebhooksPanel';

const NonSsoApp = () => {
	useEffect(() => {
		document.title = 'Socotra - Tenant Administration v2';
	});

	const configData = useConfigContext();
	const { loadConfigFromUserData } = configData;
	const authData = useAuthContext();
	const { tenantAuthorizationData, setTenantAuth } = authData;
	const authToken = authData?.adminAuthData?.authorizationToken;
	const rolesAndPermissionsEnabled =
		authData?.tenantAuthorizationData?.configProperties?.[
			ROLES_AND_PERMISSION_ENABLED
		];
	const tablesAndStaticDocsEnabled =
		authData?.tenantAuthorizationData?.configProperties?.[
			TABLES_AND_STATIC_DOCS_ENABLED
		];
	const [loading, setLoading] = React.useState(false);
	const [error, setError] = React.useState(false);
	const [currentTenant] = React.useState<TenantResponse>(
		localStorage.getItem('currentTenant')
			? (JSON.parse(localStorage.getItem('currentTenant')) as TenantResponse)
			: null,
	);

	const loadConfig = async (tenantAuthData: TenantAuthorizationData) => {
		try {
			await loadConfigFromUserData(tenantAuthData);
		} catch (e) {
			console.error(e);
			setError(true);
		}
	};

	// Get new tenant authorization token
	useEffect(() => {
		const getTenantAuthToken = async () => {
			setLoading(true);
			try {
				const res = await TenantsAPI.authorizeTenant(authToken, {
					tenantId: currentTenant.locator,
				});
				await loadConfig(res);
				await setTenantAuth(res);
			} catch (e) {
				console.error(e);
				setError(true);
			} finally {
				setLoading(false);
			}
		};
		if (!tenantAuthorizationData && !loading) {
			getTenantAuthToken();
		}
	});

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Routes>
				<Route
					path='admin'
					element={
						<NonSsoLayout
							authData={authData}
							currentTenant={currentTenant}
							loading={loading || !authData?.tenantAuthorizationData}
							error={error}
						/>
					}
				>
					<Route path='users' element={<Users />} />
					<Route path='integrations' element={<Integrations />}>
						<Route path={'external-raters'} element={<ExternalRatersPanel />} />
						<Route path={'data-autofills'} element={<DataAutofillsPanel />} />
						<Route path={'webhooks'} element={<WebhooksPanel />} />
					</Route>
					<Route path='logs' element={<ScriptLogs />} />
					<Route path='account' element={<AccountDetails />} />
					{tablesAndStaticDocsEnabled && (
						<>
							<Route path='tables' element={<Tables />} />
							<Route path='static-docs' element={<StaticDocuments />} />
						</>
					)}
					{rolesAndPermissionsEnabled && (
						<>
							<Route path='roles/edit/:roleName' element={<EditRole />} />
							<Route path='roles/new' element={<AddRole />} />
							<Route path='roles' element={<Roles />} />
						</>
					)}
					<Route path='resource-groups' element={<ResourceGroups />} />
					<Route path='logout' element={<Logout authData={authData} />} />
					<Route path='*' element={<Navigate to='/admin/users' />} />
				</Route>
				<Route path='*' element={<Navigate to='/admin/users' />} />
			</Routes>
		</LocalizationProvider>
	);
};

export default NonSsoApp;
